import { Element } from '../Element';

/**
 * ShapeElement represents a shape on the timeline
 * Aligned with Creatomate properties and structure
 */
export class ShapeElement extends Element {
  private _path: string = 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z';
  private _shape_type: string = 'rectangle';

  constructor(props: Partial<ShapeElement> = {}) {
    super({ ...props, type: 'shape' });

    // Set shape-specific properties
    this._path = props.path ?? 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z';
    this._shape_type = props.shape_type ?? 'rectangle';

    // // Shape elements can have null width and height to use unboxed coordinates
    // if (props.width === null) this.update({ width: null });
    // if (props.height === null) this.update({ height: null });
  }

  // Getters and setters
  get path(): string {
    return this._path;
  }

  set path(value: string) {
    this._path = value;
  }

  get shape_type(): string {
    return this._shape_type;
  }

  set shape_type(value: string) {
    this._shape_type = value;
  }

  /**
   * Creates a deep clone of this shape element
   * Override the base Element.clone() to ensure we create a ShapeElement, not a generic Element
   */
  override clone(): ShapeElement {
    const el = new ShapeElement(this.getState());

    return el;
  }

  override defaultFor(prop: string): string | null | undefined {
    // Shapes default width/height to 50% boxes (can be null for unboxed coords).
    if (prop === 'width' || prop === 'height') return '50%';
    return super.defaultFor(prop);
  }

  protected override getAdditionalProperties(): Record<string, any> {
    // Include all shape-specific properties in the JSON
    const props: Record<string, any> = {
      // Always include path and shape_type properties, even if null
      path: this._path,
      shape_type: this._shape_type,
    };

    return props;
  }

  /**
   * Creates a ShapeElement from JSON data
   */
  static override fromJSON(json: Record<string, any>): ShapeElement {
    // Parse time-based properties that may be formatted as strings
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    return new ShapeElement({
      // Base element properties
      id: json.id,
      name: json.name ?? null,
      track: json.track ?? null,
      time: timeValue,
      duration: parsedDuration,
      type: 'shape', // Force shape type
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: json.x ?? '50%',
      y: json.y ?? '50%',
      x_anchor: json.x_anchor ?? '50%',
      y_anchor: json.y_anchor ?? '50%',
      x_rotation: json.x_rotation ?? '0',
      y_rotation: json.y_rotation ?? '0',
      z_rotation: json.z_rotation ?? '0',
      x_skew: json.x_skew ?? '0',
      y_skew: json.y_skew ?? '0',
      z_index: json.z_index ?? null,
      width: json.width ?? '50%', // Can be null for unboxed coordinates
      height: json.height ?? '50%', // Can be null for unboxed coordinates
      fill_color: json.fill_color ?? null,
      opacity: json.opacity ?? '100%',
      // metadata removed from base Element class

      // Shape-specific properties
      shape_type: json.shape_type,
      path: json.path ?? null,
      border_radius: json.border_radius ?? '0 vmin',
      stroke_color: json.stroke_color ?? null,
      stroke_width: json.stroke_width ?? '0 vmin',
      stroke_start: json.stroke_start ?? '0%',
      stroke_end: json.stroke_end ?? '100%',
      stroke_offset: json.stroke_offset ?? '0%',
      shadow_color: json.shadow_color ?? null,
      shadow_blur: json.shadow_blur ?? '0 vmin',
      shadow_x: json.shadow_x ?? '0 vmin',
      shadow_y: json.shadow_y ?? '0 vmin',

      // Animation and effects are handled separately
      animations: Array.isArray(json.animations) ? json.animations : [],
    });
  }

  /**
   * Captures the mutable state of the shape element.
   * Extends the base Element.getState() to include shape-specific properties.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, any> {
    // Get the base element state first
    const baseState = super.getState();

    // Add shape-specific properties
    return {
      ...baseState,
      path: this._path,
      shape_type: this._shape_type,
    };
  }

  /**
   * Restores the shape element's mutable state from a state record.
   * Extends the base Element.restoreState() to handle shape-specific properties.
   * @param state The state to restore.
   */
  override restoreState(state: Record<string, any>): void {
    // First, restore base element properties
    super.restoreState(state);

    // Then restore shape-specific properties
    if (state.path !== null) this._path = state.path;
    if (state.shape_type !== null) this._shape_type = state.shape_type;
  }

  override toJSON(formatTime: Boolean = false): Record<string, any> {
    const json: Record<string, any> = {
      ...super.toJSON(formatTime),
      ...(this._shape_type !== null && { shape_type: this._shape_type }),
      ...(this._path !== null && { path: this._path }),
    };
    return json;
  }
}
